import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Controller, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import SydModal from '../../../../../../commonDesign/SydModal'
import SydModalActions from '../../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../../commonDesign/Button'
import SydLabel, { hookFormErrorAdapter } from '../../../../../../commonDesign/SydLabel'
import SydDatePicker from '../../../../../../commonDesign/SydDatePicker'
import Alert from '../../../../../../atoms/Alert'
import { ADMIN_ROUTES, ALERT_SEVERITY, ALERT_VARIANT } from '../../../../../../../constants'
import { useCreateStatusReportBatchMutation } from '../../../../../../../api/groups'

function BatchRunModal ({
  open,
  onClose,
  statusTemplateId,
  statusTemplateName,
  selectedClients
}) {
  const [error, setError] = useState(null)
  const history = useHistory()

  const form = useForm({
    defaultValues: {
      asOfDate: dayjs().add(-1, 'days').format('YYYY-MM-DD')
    }
  })

  const { mutateAsync, isLoading } = useCreateStatusReportBatchMutation()

  const handleClose = useCallback(() => {
    form.reset()
    setError(null)
    onClose?.()
  }, [form, onClose])

  const onSubmit = async (formData) => {
    try {
      setError(null)
      const clientIds = selectedClients.map(c => c.clientId)
      const result = await mutateAsync({
        statusTemplateId,
        clientIds,
        asOfDate: dayjs(formData.asOfDate).format('YYYY-MM-DD')
      })
      handleClose()
      if (result?.batchId) {
        history.push(`${ADMIN_ROUTES.STATUS_REPORTS.BATCHES.HOME}/${result.batchId}`)
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create batch. Please try again.')
    }
  }

  return (
    <SydModal
      open={open}
      onClose={handleClose}
      title='Batch Run Status Reports'
      subtitle={`Run status reports for ${selectedClients?.length ?? 0} selected client(s)`}
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-4'>
        {error && (
          <Alert variant={ALERT_VARIANT.standard} severity={ALERT_SEVERITY.error}>
            {error}
          </Alert>
        )}

        <div className='flex flex-col gap-5'>
          <div className='font-size-sm font-weight-light'>
            This will create a batch to run status reports for <strong>{selectedClients?.length ?? 0}</strong> client{selectedClients?.length !== 1 ? 's' : ''} using template <strong>{statusTemplateName}</strong>.
          </div>

          <Controller
            control={form.control}
            name='asOfDate'
            rules={{
              required: 'Please select a date',
              validate: {
                notFuture: (value) => {
                  const selected = dayjs(value)
                  const today = dayjs()
                  return selected.isBefore(today) || selected.isSame(today, 'day') || 'Date cannot be in the future'
                }
              }
            }}
            render={({ field, fieldState }) => (
              <SydLabel
                label='As Of Date'
                description='The date for all status reports in this batch'
                error={hookFormErrorAdapter(form, fieldState)}
              >
                <SydDatePicker
                  {...field}
                  size='sm'
                />
              </SydLabel>
            )}
          />
        </div>

        <SydModalActions>
          <SydButton
            variant='secondary'
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </SydButton>

          <SydButton
            priority='main'
            variant='primary'
            type='submit'
            disabled={isLoading || !selectedClients?.length}
            processing={isLoading}
          >
            {isLoading ? 'Creating Batch...' : 'Run Batch'}
          </SydButton>
        </SydModalActions>
      </form>
    </SydModal>
  )
}

BatchRunModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  statusTemplateId: PropTypes.number.isRequired,
  statusTemplateName: PropTypes.string,
  selectedClients: PropTypes.array.isRequired
}

export default BatchRunModal
